import { useCallback, useMemo, useRef } from "react";

type Pending<TPayload> = {
  payload: TPayload;
  committed: boolean;
  timeout: ReturnType<typeof setTimeout>;
};

export const useUndoableAction = <TPayload>() => {
  const ref = useRef<Pending<TPayload> | null>(null);

  const schedule = useCallback(
    (payload: TPayload, delay: number, onCommit: (payload: TPayload) => void) => {
      if (ref.current) {
        clearTimeout(ref.current.timeout);
      }

      const pending: Pending<TPayload> = {
        payload,
        committed: false,
        timeout: setTimeout(() => {
          if (ref.current !== pending) {
            return;
          }
          pending.committed = true;
          ref.current = null;
          onCommit(payload);
        }, delay),
      };
      ref.current = pending;

      return {
        isCurrent: () => ref.current === pending,
      };
    },
    [],
  );

  const cancel = useCallback(() => {
    if (ref.current) {
      clearTimeout(ref.current.timeout);
      ref.current = null;
    }
  }, []);

  const commitNow = useCallback((onCommit: (payload: TPayload) => void) => {
    const pending = ref.current;
    if (!pending || pending.committed) {
      return;
    }
    clearTimeout(pending.timeout);
    pending.committed = true;
    ref.current = null;
    onCommit(pending.payload);
  }, []);

  return useMemo(() => ({ schedule, cancel, commitNow }), [cancel, commitNow, schedule]);
};
